/*
 * -----------------------------------------------------------------------
 *  <copyright file="preview-table.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useGridCells, useLazyQueryConvertToJson, useLocalState, useQueryConvertToJson } from 'hooks';
import { EXECUTE_DATA_REVIEW_TABLE, PREVIEW_DATA_REVIEW_TABLE } from 'queries/central-monitoring';
import { DataReviewTableFields } from 'queries/generated/fragments.generated';
import {
    DataReviewTableColumnLevel,
    DataReviewTableColumnType,
    DataReviewType,
    PanelFieldType,
} from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { modifyObjectForExport } from 'utils/convertor';
import { getFieldName } from 'utils/shared';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import Loading from 'views/components/core/loading';
import { AllRoutes } from '@utils/router/all-routes';
import DataReviewTableApi from '@apis/bff-api/central-monitoring/data-review-table';

interface IPreviewTable {
    table?: any;
    studyRef?: string;
}

interface IViewTable {
    study: any;
    currentTable: DataReviewTableFields;
    dataSetId?: string;
    siteId?: string;
    sitesStatusesToExclude?: string[];
    savedData?: any;
    setSavedData?: Function;
    onClose?: () => void;
}

interface IPreviewResult {
    result?: any;
    state: any;
    error?: string;
}

const LegacyPreviewTable = ({ table }: IPreviewTable) => {
    const { convertedData, loading, error } = useQueryConvertToJson(PREVIEW_DATA_REVIEW_TABLE, {
        table: {
            active: true,
            columns: table.columns?.map((column: any) => ({
                customRowDataSourceName: column.customRowDataSourceName,
                dataField: column.dataField,
                denominatorField: column.denominatorField,
                heading: column.heading,
                level: column.level,
                numeratorField: column.numeratorField,
                dataSource: column.dataSource || table.dataSource,
                type: column.type,
            })),
            description: table.description,
            id: table.id,
            name: table.name,
            recordId: table.recordId,
            dataSource: table.dataSource,
            studyId: table.study.id,
            type: table.type,
        },
    });

    if (loading) return <Loading />;

    if (error || convertedData?.error) return <h1>Error loading preview: {error?.message ?? convertedData?.error}</h1>;

    return (
        <DisplayDataReviewTableData
            columns={convertedData?.result ?? table.columns}
            tableName={table?.name}
            study={table.study}
        />
    );
};

// We need to keep this in sync with the BE code in DataReviewType.cs
//
// We use this to convert from the Graphql enum string to the integer value of the enum so when we deserialize
// on the backend we can convert from the number to the enum value. If we use the string from the FE it doesn't match
// the backend strings and fails.
function DataReviewTypeValue(valueName: DataReviewType) {
    switch (valueName) {
        case DataReviewType.STUDY:
            return 0;
        case DataReviewType.COUNTRY:
            return 1;
        case DataReviewType.SITE:
            return 2;
        case DataReviewType.SUBJECT:
            return 3;
        case DataReviewType.DATA:
            return 4;
        default:
            console.error(`Unsupported value '${valueName}' for DataReviewTypeValue in Data Table Preview`);
    }
}

// We need to keep this in sync with the BE code in PanelFieldType.cs
//
// We use this to convert from the Graphql enum string to the integer value of the enum so when we deserialize
// on the backend we can convert from the number to the enum value. If we use the string from the FE it doesn't match
// the backend strings and fails.
function PanelFieldValue(valueName: PanelFieldType) {
    switch (valueName) {
        case PanelFieldType.STUDY_RATE:
            return 0;
        case PanelFieldType.STUDY_SITE_MEAN:
            return 1;
        case PanelFieldType.COUNTRY_SITE_MEAN:
            return 2;
        case PanelFieldType.SITE_RATE:
            return 3;
        default:
            console.error(`Unsupported value '${valueName}' for PanelFieldValue in Data Table Preview`);
    }
}

// We need to keep this in sync with the BE code in DataReviewTableColumnLevel.cs
//
// We use this to convert from the Graphql enum string to the integer value of the enum so when we deserialize
// on the backend we can convert from the number to the enum value. If we use the string from the FE it doesn't match
// the backend strings and fails.
function ColumnLevelValue(valueName: DataReviewTableColumnLevel) {
    switch (valueName) {
        case DataReviewTableColumnLevel.NONE:
            return 0;
        case DataReviewTableColumnLevel.STUDY:
            return 1;
        case DataReviewTableColumnLevel.COUNTRY:
            return 2;
        case DataReviewTableColumnLevel.SITE:
            return 3;
        case DataReviewTableColumnLevel.SUBJECT:
            return 4;
        default:
            console.error(`Unsupported value '${valueName}' for ColumnLevelFieldValue in Data Table Preview`);
    }
}

// We need to keep this in sync with the BE code in DataReviewTableColumnType.cs
//
// We use this to convert from the Graphql enum string to the integer value of the enum so when we deserialize
// on the backend we can convert from the number to the enum value. If we use the string from the FE it doesn't match
// the backend strings and fails.
function ColumnTypeValue(valueName: DataReviewTableColumnType) {
    switch (valueName) {
        case DataReviewTableColumnType.STATIC:
            return 0;
        case DataReviewTableColumnType.STANDARD_CALCULATION:
            return 1;
        case DataReviewTableColumnType.PERCENTAGE:
            return 2;
        case DataReviewTableColumnType.RATE:
            return 3;
        case DataReviewTableColumnType.MAX:
            return 4;
        case DataReviewTableColumnType.MIN:
            return 5;
        case DataReviewTableColumnType.SUM:
            return 6;
        case DataReviewTableColumnType.COUNT:
            return 7;
        case DataReviewTableColumnType.COUNTRY_MEAN:
            return 8;
        case DataReviewTableColumnType.STUDY_MEAN:
            return 9;
        case DataReviewTableColumnType.SITE_MEAN:
            return 10;
        case DataReviewTableColumnType.COUNTRY_MEDIAN:
            return 11;
        case DataReviewTableColumnType.STUDY_MEDIAN:
            return 12;
        case DataReviewTableColumnType.SITE_MEDIAN:
            return 13;
        case DataReviewTableColumnType.PERCENTILE:
            return 14;
        case DataReviewTableColumnType.PANEL:
            return 15;
        default:
            console.error(`Unsupported value '${valueName}' for ColumnTypeValue in Data Table Preview`);
    }
}

const PreviewTable = ({ table, studyRef }: IPreviewTable) => {
    const tableConfig = {
        active: true,
        columns: table.columns?.map((column: any) => ({
            customRowDataSourceName: column.customRowDataSourceName,
            dataField: column.dataField,
            denominatorField: column.denominatorField,
            heading: column.heading,
            level: ColumnLevelValue(column.level),
            numeratorField: column.numeratorField,
            dataSource: column.dataSource || table.dataSource,
            type: ColumnTypeValue(column.type),
            panelId:
                column.panelId === undefined || column.panelId === null ? Constants.ObjectId.Empty : column.panelId,
            panelField: PanelFieldValue(column.panelField),
        })),
        description: table.description,
        id: table.id,
        name: table.name,
        recordId: table.recordId,
        dataSource: table.dataSource,
        studyId: table.study.id,
        type: DataReviewTypeValue(table.type),
    };

    const [data, setData] = React.useState<IPreviewResult | undefined>(undefined);
    const { trigger: preview, isMutating: isStarting } = DataReviewTableApi.usePreview(studyRef!);

    useEffect(() => {
        preview(JSON.stringify(tableConfig))
            .then((payload: string) => {
                const result = JSON.parse(payload);
                setData(result);
            })
            .catch(() => {
                setData({ result: undefined, state: undefined, error: 'Exception' });
            });
    }, []); // So we only run once when loading

    if (data === undefined || isStarting) return <Loading />;

    if (data.error) return <h1>Error loading preview: {data.error}</h1>;

    return (
        <DisplayDataReviewTableData
            columns={data.result ?? table.columns}
            tableName={table?.name}
            study={table.study}
        />
    );
};

const ViewTable = ({
    study,
    currentTable,
    dataSetId,
    siteId,
    sitesStatusesToExclude,
    savedData,
    setSavedData,
    onClose,
}: IViewTable) => {
    const [loadDataReviewTable, { convertedData, loading, error }] =
        useLazyQueryConvertToJson(EXECUTE_DATA_REVIEW_TABLE);

    useEffect(() => {
        if (currentTable) loadDataReviewTable({ variables: { id: currentTable?.id, studyId: study?.id, dataSetId } });
    }, [currentTable]);

    useEffect(() => {
        if (convertedData?.result) setSavedData(convertedData?.result);
    }, [convertedData]);

    if (loading) return <Loading />;

    if (error || convertedData?.error)
        return <h1>Error loading data review table: {error?.message ?? convertedData?.error}</h1>;

    return (
        <DisplayDataReviewTableData
            columns={savedData ?? currentTable?.columns}
            siteId={siteId}
            excelExport
            tableName={currentTable?.name}
            study={study}
            sitesStatusesToExclude={sitesStatusesToExclude}
            onClose={onClose}
        />
    );
};

const DisplayDataReviewTableData = ({
    siteId,
    columns,
    excelExport = false,
    tableName,
    study,
    sitesStatusesToExclude,
    onClose,
}: any) => {
    const { StandardCell, DateCell, YesNoCell, RoundedNumberCell, PartialDateCell } = useGridCells();
    const navigate = useNavigate();
    const location = useLocation();
    const [exportColumns, setExportColumns] = useState([]);
    const [gridColumns, setGridColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const { state } = useLocalState();

    const getCellType = (col: any, fieldName?: string) => {
        switch (col.type) {
            case Constants.FieldDefinitionFieldTypes.BOOL:
                return YesNoCell;

            case Constants.FieldDefinitionFieldTypes.DATE:
                return DateCell;
            case Constants.FieldDefinitionFieldTypes.PARTIAL_DATE:
                return PartialDateCell(fieldName);

            case Constants.FieldDefinitionFieldTypes.INT:
                return null;

            case Constants.FieldDefinitionFieldTypes.FLOAT:
                if (Constants.DataReviewTableColumnType[col.columnType] === DataReviewTableColumnType.PERCENTAGE)
                    return RoundedNumberCell(0);
                return RoundedNumberCell(2);

            default:
                return StandardCell();
        }
    };

    const getType = (type: number) => {
        switch (type) {
            case Constants.FieldDefinitionFieldTypes.BOOL:
                return 'bool';

            case Constants.FieldDefinitionFieldTypes.INT:
            case Constants.FieldDefinitionFieldTypes.FLOAT:
                return 'numeric';

            case Constants.FieldDefinitionFieldTypes.DATE:
            case Constants.FieldDefinitionFieldTypes.PARTIAL_DATE:
                return 'date';
            default:
                return null;
        }
    };

    const getFilterType = (col: any) =>
        col.type === Constants.FieldDefinitionFieldTypes.BOOL
            ? { filterUi: GridColumnMenuActiveFilter }
            : { filter: getType(col.type) };

    useEffect(() => {
        const cols = columns ?? [];
        const rowsData: any = [];
        const exportArray = [];
        let fieldNum = 0;
        let siteIndices = [] as any;
        let isSiteIdPresent = false;
        const result: any[] = [];

        const siteIdIndex = cols.map((c: any) => c.heading).indexOf('Site Id');
        isSiteIdPresent = siteIdIndex >= 0;

        // If a siteId is specified
        if (siteId) {
            siteIndices = [];
            // Find out which column is 'Site Id', if it exists
            if (siteIdIndex >= 0) {
                // Find out which indices are the correct siteId
                (cols[siteIdIndex].values || []).forEach((val: string, index: number) => {
                    if (val === siteId) siteIndices.push(index);
                });
            }
        }
        for (let i = 0; i < cols.length; i++) {
            if (cols[i].heading !== 'Site Id') {
                // Add the column

                result.push({
                    title: cols[i].heading,
                    id: `id-${cols[i].heading}`,
                    field:
                        cols[i].type === Constants.FieldDefinitionFieldTypes.PARTIAL_DATE
                            ? `field-${fieldNum}.time`
                            : `field-${fieldNum}`,
                    minWidth: 190,
                    cells: { data: getCellType(cols[i], `field-${fieldNum}`) },
                    ...getFilterType(cols[i]),
                    isWrapHeader: true,
                    tooltipValue: cols[i].heading,
                });

                exportArray.push({
                    title: cols[i].heading,
                    id: `id-${cols[i].heading}`,
                    field: getFieldName(cols[i].type, `field-${fieldNum}`),
                });
            }

            let rowIndex = 0;
            if (cols[i].values) {
                for (let j = 0; j < cols[i].values.length; j++) {
                    let includeSite = true;
                    if (isSiteIdPresent) {
                        const site = state.studyData?.sites?.find(
                            (element: any) => cols[0].values[j] === element.Fields['Site.id'],
                        );
                        if (site)
                            includeSite =
                                (sitesStatusesToExclude ?? study.sitesStatusesToExclude).indexOf(
                                    site.Fields['Site.status'],
                                ) < 0;
                    }
                    if ((!siteIndices.length || siteIndices.indexOf(j) >= 0) && includeSite) {
                        if (!rowsData[rowIndex]) rowsData[rowIndex] = {};

                        if (cols[i].heading.match('Site Id')) {
                            rowsData[rowIndex].siteId = cols[i].values[j];
                            const site = state?.studyData?.sites?.find(
                                (item: any) => item.Fields['Site.id'] === cols[i].values[j],
                            );
                            if (site) {
                                rowsData[rowIndex].name = site.Fields['Site.name'];
                                rowsData[rowIndex].status = site.Fields['Site.status'];
                            }
                        } else rowsData[rowIndex][`field-${fieldNum}`] = cols[i].values[j];
                        modifyObjectForExport(
                            rowsData[rowIndex],
                            cols[i].type,
                            fieldNum,
                            cols[i].values[j],
                            cols[i].columnType,
                        );
                        rowIndex++;
                    }
                }
            }
            fieldNum++;
        }

        setRows(rowsData);
        setGridColumns(result);
        setExportColumns(exportArray);
    }, [columns, siteId, sitesStatusesToExclude, study, onClose]);

    let rowActions = null;
    const isFieldPresented = rows.findIndex((item: any) => item.siteId && item.name) >= 0;
    if (!location.pathname.match(AllRoutes.path('cm-site-dashboard')) && isFieldPresented && sitesStatusesToExclude)
        rowActions = (item: any) => [
            {
                label: 'cm:viewSiteDashboard',
                icon: ['far', 'eye'],
                onClick: () => {
                    if (onClose !== undefined) {
                        onClose();
                    }
                    NavigationService.setViewLegacySiteDashboard();
                    navigate(AllRoutes.path('cm-site-dashboard') as string, {
                        state: {
                            skipSelectDate: true,
                            showDashboard: true,
                            siteId: item.siteId as string | null | undefined,
                        },
                    });
                },
                enabled: sitesStatusesToExclude?.indexOf(item.status) < 0 ?? true,
            },
        ];

    if (!rows.length) {
        return <>No Data</>;
    }

    return (
        <ExpandableGrid
            customKey="preview-table-data-review"
            id="previewTableDataReview"
            items={rows}
            groupable
            showPager
            scrollable="scrollable"
            reorderable
            resizable
            addScroll={false}
            showFirstColumn={!!rowActions}
            pageable={false}
            rowActions={rowActions}
            options={{
                allowExcelExport: excelExport,
                allowExpandCollapseAll: false,
                useFilterChips: true,
            }}
            excelExportOptions={{
                name: tableName,
                fileName: tableName,
                columns: exportColumns,
            }}
            detail={null}
            columns={gridColumns}
        />
    );
};

export default DisplayDataReviewTableData;

export { PreviewTable, LegacyPreviewTable, ViewTable };
